import React, { FC } from 'react';

import { ComponentCount, ViewOnlyFilter } from 'models';

import { InlineFiltersCable } from 'components/component-overview/inline-filters/filters/InlineFilters.Cable';
import { InlineFiltersMeasurementPlaceholder } from 'components/inline-filters/InlineFilters.MeasurementPlaceholder';
import { InlineFiltersMeasurementWrapper } from 'components/inline-filters/InlineFilters.MeasurementWrapper';
import { InlineFiltersNumber } from 'components/inline-filters/InlineFilters.Number';
import { InlineFiltersSection, InlineFiltersSectionWithPopover } from 'components/inline-filters/InlineFilters.Section';
import { InlineFiltersSectionRemove } from 'components/inline-filters/InlineFilters.SectionRemove';
import { InlineFiltersSwitcher } from 'components/inline-filters/InlineFilters.Switcher';
import { InlineFiltersWrapper, InlineFiltersWrapperProps } from 'components/inline-filters/InlineFilters.Wrapper';

import { InlineFiltersBack } from './components/InlineFilters.Back';
import { InlineFiltersMeasurementValue } from './components/InlineFilters.MeasurementValue';
import { InlineFiltersPortWrapper } from './components/InlineFilters.PortWrapper';
import { InlineFiltersAIEnabled } from './filters/InlineFilters.AIEnabled';
import { InlineFiltersApplication } from './filters/InlineFilters.Application';
import { InlineFiltersArchived } from './filters/InlineFilters.Archived';
import { InlineFiltersBreakerType } from './filters/InlineFilters.BreakerType';
import { InlineFiltersCapacity } from './filters/InlineFilters.Capacity';
import { InlineFiltersCharging } from './filters/InlineFilters.Charging';
import { InlineFiltersClearButton } from './filters/InlineFilters.ClearButton';
import { InlineFiltersCompliances } from './filters/InlineFilters.Compliances';
import { InlineFiltersCurrent } from './filters/InlineFilters.Current';
import { InlineFiltersDistributor } from './filters/InlineFilters.Distributor';
import { InlineFiltersEvent } from './filters/InlineFilters.Event';
import { InlineFiltersInAppSupport } from './filters/InlineFilters.InAppSupport';
import { InlineFiltersIsolated } from './filters/InlineFilters.Isolated';
import { InlineFiltersLeadTime } from './filters/InlineFilters.LeadTime';
import { InlineFiltersManufacturer } from './filters/InlineFilters.Manufacturer';
import { InlineFiltersMoreButton } from './filters/InlineFilters.MoreButton';
import { InlineFiltersPorts } from './filters/InlineFilters.Ports';
import { InlineFiltersPower } from './filters/InlineFilters.Power';
import { InlineFiltersRating } from './filters/InlineFilters.Rating';
import { InlineFiltersPowerFlowDirection } from './filters/InlineFilters.PowerFlowDirection';
import { InlineFiltersMaxPrice, InlineFiltersPriceFlag } from './filters/InlineFilters.Price';
import { InlineFiltersPurpose } from './filters/InlineFilters.Purpose';
import { InlineFiltersRegionAvailability } from './filters/InlineFilters.RegionAvailability';
import { InlineFiltersSearch } from './filters/InlineFilters.Search';
import { InlineFiltersSort } from './filters/InlineFilters.Sort';
import { InlineFiltersType } from './filters/InlineFilters.Type';
import { InlineFiltersVoltage } from './filters/InlineFilters.Voltage';
import { InlineFiltersVoltageType } from './filters/InlineFilters.VoltageType';

export type InlineFiltersProps = InlineFiltersWrapperProps & {
    componentCount?: ComponentCount;
    hideFilters?: (
        | 'search'
        | 'type'
        | 'breakerType'
        | 'manufacturer'
        | 'distributor'
        | 'power'
        | 'portPower'
        | 'capacity'
        | 'ports'
        | 'archived'
        | 'aiEnabled'
        | 'inAppSupport'
        | 'clear'
        | 'regionAvailability'
        | 'compliances'
        | 'application'
        | 'charging'
        | 'maxPrice'
        | 'hasPrice'
        | 'hasLeadTime'
        | 'cable'
        | 'more'
        | 'event'
        | 'sort'
    )[];
    viewOnlyFilters?: ViewOnlyFilter[];
};

const InlineFilters: FC<InlineFiltersProps> & {
    Wrapper: typeof InlineFiltersWrapper;
    Section: typeof InlineFiltersSection;
    SectionWithPopover: typeof InlineFiltersSectionWithPopover;
    RemoveButton: typeof InlineFiltersSectionRemove;
    MeasurementWrapper: typeof InlineFiltersMeasurementWrapper;
    MeasurementPlaceholder: typeof InlineFiltersMeasurementPlaceholder;
    MeasurementValue: typeof InlineFiltersMeasurementValue;
    Number: typeof InlineFiltersNumber;
    PortWrapper: typeof InlineFiltersPortWrapper;
    Switcher: typeof InlineFiltersSwitcher;
    Type: typeof InlineFiltersType;
    BreakerType: typeof InlineFiltersBreakerType;
    Manufacturer: typeof InlineFiltersManufacturer;
    Distributor: typeof InlineFiltersDistributor;
    RegionAvailability: typeof InlineFiltersRegionAvailability;
    Compliances: typeof InlineFiltersCompliances;
    Application: typeof InlineFiltersApplication;
    Ports: typeof InlineFiltersPorts;
    Charging: typeof InlineFiltersCharging;
    Voltage: typeof InlineFiltersVoltage;
    Power: typeof InlineFiltersPower;
    Current: typeof InlineFiltersCurrent;
    PowerFlowDirection: typeof InlineFiltersPowerFlowDirection;
    Isolated: typeof InlineFiltersIsolated;
    Purpose: typeof InlineFiltersPurpose;
    VoltageType: typeof InlineFiltersVoltageType;
    Rating: typeof InlineFiltersRating;
    Capacity: typeof InlineFiltersCapacity;
    Archived: typeof InlineFiltersArchived;
    Sort: typeof InlineFiltersSort;
    Search: typeof InlineFiltersSearch;
    AIEnabled: typeof InlineFiltersAIEnabled;
    InAppSupport: typeof InlineFiltersInAppSupport;
    Clear: typeof InlineFiltersClearButton;
    More: typeof InlineFiltersMoreButton;
    Back: typeof InlineFiltersBack;
    MaxPrice: typeof InlineFiltersMaxPrice;
    PriceFlag: typeof InlineFiltersPriceFlag;
    LeadTime: typeof InlineFiltersLeadTime;
    Event: typeof InlineFiltersEvent;
    Cable: typeof InlineFiltersCable;
} = ({ componentCount, hideFilters, viewOnlyFilters, ...props }) => {
    return (
        <InlineFilters.Wrapper {...props}>
            {!hideFilters?.includes('event') && <InlineFilters.Event />}
            {!hideFilters?.includes('search') && <InlineFilters.Search />}
            {!hideFilters?.includes('type') && (
                <InlineFilters.Type
                    componentCount={componentCount}
                    isViewOnly={viewOnlyFilters?.includes(ViewOnlyFilter.TYPE)}
                />
            )}
            {!hideFilters?.includes('breakerType') && <InlineFilters.BreakerType />}
            {!hideFilters?.includes('manufacturer') && (
                <InlineFilters.Manufacturer isViewOnly={viewOnlyFilters?.includes(ViewOnlyFilter.MANUFACTURER)} />
            )}
            {!hideFilters?.includes('distributor') && (
                <InlineFilters.Distributor isViewOnly={viewOnlyFilters?.includes(ViewOnlyFilter.DISTRIBUTOR)} />
            )}
            {!hideFilters?.includes('cable') && <InlineFilters.Cable />}
            {!hideFilters?.includes('maxPrice') && <InlineFilters.MaxPrice />}
            {!hideFilters?.includes('hasPrice') && <InlineFilters.PriceFlag />}
            {!hideFilters?.includes('hasLeadTime') && <InlineFilters.LeadTime />}
            {!hideFilters?.includes('regionAvailability') && <InlineFilters.RegionAvailability />}
            {!hideFilters?.includes('compliances') && <InlineFilters.Compliances />}
            {!hideFilters?.includes('application') && <InlineFilters.Application />}
            {!hideFilters?.includes('aiEnabled') && <InlineFilters.AIEnabled />}
            {!hideFilters?.includes('ports') && <InlineFilters.Ports />}
            {!hideFilters?.includes('charging') && <InlineFilters.Charging />}
            {!hideFilters?.includes('power') && <InlineFilters.Rating rating="power" />}
            <InlineFilters.Rating rating="voltage" />
            <InlineFilters.Rating rating="current" />
            {!hideFilters?.includes('capacity') && <InlineFilters.Capacity />}
            {!hideFilters?.includes('archived') && <InlineFilters.Archived />}
            {!hideFilters?.includes('sort') && <InlineFilters.Sort />}

            {!hideFilters?.includes('more') && <InlineFilters.More hideFilters={hideFilters} />}
        </InlineFilters.Wrapper>
    );
};

InlineFilters.Wrapper = InlineFiltersWrapper;
InlineFilters.Section = InlineFiltersSection;
InlineFilters.SectionWithPopover = InlineFiltersSectionWithPopover;
InlineFilters.RemoveButton = InlineFiltersSectionRemove;
InlineFilters.MeasurementWrapper = InlineFiltersMeasurementWrapper;
InlineFilters.MeasurementPlaceholder = InlineFiltersMeasurementPlaceholder;
InlineFilters.MeasurementValue = InlineFiltersMeasurementValue;
InlineFilters.Number = InlineFiltersNumber;
InlineFilters.PortWrapper = InlineFiltersPortWrapper;
InlineFilters.Switcher = InlineFiltersSwitcher;

InlineFilters.Type = InlineFiltersType;
InlineFilters.BreakerType = InlineFiltersBreakerType;
InlineFilters.Manufacturer = InlineFiltersManufacturer;
InlineFilters.Distributor = InlineFiltersDistributor;
InlineFilters.RegionAvailability = InlineFiltersRegionAvailability;
InlineFilters.Compliances = InlineFiltersCompliances;
InlineFilters.Application = InlineFiltersApplication;
InlineFilters.Ports = InlineFiltersPorts;
InlineFilters.Voltage = InlineFiltersVoltage;
InlineFilters.Power = InlineFiltersPower;
InlineFilters.Charging = InlineFiltersCharging;
InlineFilters.Current = InlineFiltersCurrent;
InlineFilters.PowerFlowDirection = InlineFiltersPowerFlowDirection;
InlineFilters.Isolated = InlineFiltersIsolated;
InlineFilters.Purpose = InlineFiltersPurpose;
InlineFilters.VoltageType = InlineFiltersVoltageType;
InlineFilters.Rating = InlineFiltersRating;
InlineFilters.Capacity = InlineFiltersCapacity;
InlineFilters.Archived = InlineFiltersArchived;
InlineFilters.Sort = InlineFiltersSort;
InlineFilters.Search = InlineFiltersSearch;
InlineFilters.AIEnabled = InlineFiltersAIEnabled;
InlineFilters.InAppSupport = InlineFiltersInAppSupport;
InlineFilters.Clear = InlineFiltersClearButton;
InlineFilters.More = InlineFiltersMoreButton;
InlineFilters.Back = InlineFiltersBack;
InlineFilters.MaxPrice = InlineFiltersMaxPrice;
InlineFilters.PriceFlag = InlineFiltersPriceFlag;
InlineFilters.LeadTime = InlineFiltersLeadTime;
InlineFilters.Event = InlineFiltersEvent;
InlineFilters.Cable = InlineFiltersCable;

export { InlineFilters };
